import { z } from "zod"

import { COST_BREAKDOWN_AS400_MAX_VALUE, COST_BREAKDOWN_DECIMAL_PRECISION } from "../constants"

import { AtypicalSurchargeType, ShippingType } from "@/graphql/codegen/graphql"

const optionalCurrencyNumber = () =>
  z
    .number()
    .min(0, "Amount must be positive")
    .refine(
      (value) => {
        const decimalsString = value.toString().split(".")[1]
        return !decimalsString || decimalsString.length <= COST_BREAKDOWN_DECIMAL_PRECISION
      },
      {
        message: `Amount must have at most ${COST_BREAKDOWN_DECIMAL_PRECISION} decimal places`,
      }
    )
    .nullish()

const optionalCurrencyNumberWithRange = () =>
  z
    .number()
    .refine(
      (value) => value >= 0 && value <= COST_BREAKDOWN_AS400_MAX_VALUE,
      { message: `Amount must be between 0 and ${COST_BREAKDOWN_AS400_MAX_VALUE}` } // AS400 constraint
    )
    .refine(
      (value) => {
        const decimalsString = value.toString().split(".")[1]
        return !decimalsString || decimalsString.length <= COST_BREAKDOWN_DECIMAL_PRECISION
      },
      { message: `Amount must have at most ${COST_BREAKDOWN_DECIMAL_PRECISION} decimal places` }
    )
    .nullish()

const optionalPercentageNumber = () =>
  z
    .number()
    .min(0, "Percentage must be positive")
    .refine(
      (value) => {
        const decimalsString = value.toString().split(".")[1]
        return !decimalsString || decimalsString.length <= COST_BREAKDOWN_DECIMAL_PRECISION
      },
      {
        message: `Percentage must have at most ${COST_BREAKDOWN_DECIMAL_PRECISION} decimal places`,
      }
    )
    .nullish()

export const shippingCostItemSchema = z
  .object({
    grShippingCostId: z.string().nullish(),
    shippingType: z.nativeEnum(ShippingType).nullable(),
    cost: optionalCurrencyNumber(),
  })
  .superRefine((data, ctx) => {
    const hasCost = data.cost !== null && data.cost !== undefined

    if (hasCost && data.shippingType === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Shipping type is required when cost is provided",
        path: ["shippingType"],
      })
    }
  })

export const atypicalSurchargeItemSchema = z
  .object({
    grAtypicalSurchargeId: z.string().nullish(), // null for new items, ID for existing
    description: z.string().max(50, "Description cannot exceed 50 characters"),
    amount: optionalCurrencyNumber(),
  })
  .superRefine((data, ctx) => {
    const hasAmount = data.amount !== null && data.amount !== undefined
    const hasDescription = data.description.trim().length > 0

    if (hasAmount && !hasDescription) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Description is required when amount is provided",
        path: ["description"],
      })
    }
  })

export const costBreakdownFormSchema = z.object({
  shippingCosts: z.array(shippingCostItemSchema),
  importFees: optionalCurrencyNumber(),
  insurance: optionalCurrencyNumber(),
  mpf: optionalCurrencyNumber(),
  hmf: optionalCurrencyNumber(),
  atypicalSurcharges: z.array(atypicalSurchargeItemSchema),
  sharedGrCost: optionalCurrencyNumber(),
})

export type CostBreakdownFormValues = z.infer<typeof costBreakdownFormSchema>

const productAtypicalSurchargeItemSchema = z
  .object({
    type: z.nativeEnum(AtypicalSurchargeType).nullish(),
    description: z.string().max(50, "Description cannot exceed 50 characters").optional(),
    amount: optionalCurrencyNumber(),
  })
  .superRefine((data, ctx) => {
    const hasAmount = data.amount !== null && data.amount !== undefined
    const hasType = data.type !== null && data.type !== undefined

    if (hasAmount && !hasType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Type is required when amount is provided",
        path: ["type"],
      })
    }
  })

export const costBreakdownProductItemSchema = z.object({
  pricePerUom: optionalCurrencyNumber(),
  pricePerMetricUom: optionalCurrencyNumber(),
  bareCostPerUom: optionalCurrencyNumberWithRange(),
  bareCostPerMetricUom: optionalCurrencyNumber(),
  totalBareCost: optionalCurrencyNumber(),
  insurance: optionalCurrencyNumber(),
  tariffs: z.array(
    z.object({
      amount: optionalCurrencyNumber(),
      percentage: optionalPercentageNumber(),
    })
  ),
  duties: z
    .object({
      amount: optionalCurrencyNumber(),
      percentage: optionalPercentageNumber(),
    })
    .optional(),
  mpf: z
    .object({
      amount: optionalCurrencyNumber(),
      percentage: optionalPercentageNumber(),
    })
    .optional(),
  hmf: z
    .object({
      amount: optionalCurrencyNumber(),
      percentage: optionalPercentageNumber(),
    })
    .optional(),
  atypicalSurcharges: z.array(productAtypicalSurchargeItemSchema),
  allocatedGrCostPercentage: optionalPercentageNumber(),
  allocatedGrCostAmount: optionalCurrencyNumber(),
  allocatedGrCostPerUom: optionalCurrencyNumber(),
  lastLaidDownCostPerUom: optionalCurrencyNumber(),
  totalLaidDownCostPerUom: optionalCurrencyNumberWithRange(),
  freightAndMiscCostPerUom: optionalCurrencyNumberWithRange(),
  isCompleted: z.boolean().optional(),
})

export const costBreakdownProductsFormSchema = z.object({
  products: z.array(costBreakdownProductItemSchema),
  sharedGrCost: optionalCurrencyNumber(),
})

export type CostBreakdownProductsFormValues = z.infer<typeof costBreakdownProductsFormSchema>
